import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Modal,
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Form, Row } from "reactstrap";
import BreadCrumbs from "../../../components/BreadCrumbs";
import ButtonComponent from "../../../components/ButtonComponent";
import ControlledRowTextField from "../../../components/ControlledRowTextField";
import ControlledRowTextFieldAddress from "../../../components/ControlledRowTextFieldAddress";
import Map from "../../../components/LocationSelectorModal";
import {
  Organization,
  useCreateOrganizationMutation,
} from "../../../redux/services/orgService";

// interface FormData {
//   name: string;
//   code: string;
//   status: string | { value: string; label: string };
//   supportsDeliveryPoint: boolean;
//   deliveryPointName: string;
//   deliveryAddress: string;
//   deliveryLatitude: number;
//   deliveryLongitude: number;
//   deliveryContactName: string;
//   deliveryContactPhone: string;
//   allowCreditOrders: boolean;
//   creditLimit: number;
//   creditTermsDays: number;
//   memberCreditLimit: number;
// }

// const STATUS_OPTIONS = [
//   { value: "Approved", label: "Approved" },
//   { value: "Deleted", label: "Deleted" },
// ];

const CreateOrg = () => {
  const navigate = useNavigate();
  const [createOrganization, { isLoading: isCreating }] =
    useCreateOrganizationMutation();

  const [showModal, setShowModal] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    watch,
    register,
  } = useForm<Organization>({
    defaultValues: {
      displayName: "",
      code: "",
      status: "Approved",
      supportsDeliveryPoint: false,
      deliveryAddress: "",
      deliveryLatitude: 0,
      deliveryLongitude: 0,
      deliveryContactName: "",
      deliveryContactPhone: "",
      allowCreditOrders: false,
      creditLimit: 0,
      creditTermsDays: 0,
      memberCreditLimit: 0,
    },
  });

  // Watch for conditional field changes
  const supportsDeliveryPoint = watch("supportsDeliveryPoint");
  const allowCreditOrders = watch("allowCreditOrders");

  const handleLocationSave = (
    latitude: number,
    longitude: number,
    address: string
  ) => {
    setValue("deliveryLatitude", latitude);
    setValue("deliveryLongitude", longitude);
    setValue("deliveryAddress", address);
  };


  // Add this function to handle manual save
  const handleManualLocationSave = () => {
    setShowModal(false);
    toast.success("Location saved successfully");
  };

  const onSubmit = async (data: Organization) => {
    setIsSubmitLoading(true);

    // Prepare payload according to your request structure
    const payload: Organization = {
      displayName: data.displayName,
      code: data.code,
      status: typeof data.status === "object" ? data.status.value : "Approved",
      supportsDeliveryPoint: data.supportsDeliveryPoint,
      ...(data.supportsDeliveryPoint && {
        deliveryAddress: data.deliveryAddress || "",
        deliveryLatitude: data.deliveryLatitude
          ? parseFloat(data.deliveryLatitude.toString())
          : 0,
        deliveryLongitude: data.deliveryLongitude
          ? parseFloat(data.deliveryLongitude.toString())
          : 0,
        deliveryContactName: data.deliveryContactName || "",
        deliveryContactPhone: data.deliveryContactPhone || "",
      }),
      allowCreditOrders: data.allowCreditOrders,
      ...(data.allowCreditOrders && {
        creditLimit: data.creditLimit
          ? parseFloat(data.creditLimit.toString())
          : 0,
        creditTermsDays: data.creditTermsDays
          ? parseFloat(data.creditTermsDays.toString())
          : 0,
        memberCreditLimit: data.memberCreditLimit
          ? parseFloat(data.memberCreditLimit.toString())
          : 0,
      }),
    };


    try {
      await createOrganization(payload).unwrap();

      toast.success("Organization Created Successfully.");
      setIsSubmitLoading(false);
      navigate("/organizations/organization-list");
      } catch (error: any) {
      setIsSubmitLoading(false);
      
      // Check for 409 Conflict status code
      if (error?.originalStatus === 409) {
        toast.error(`An organization with the same name or code cannot be created.`);
      } else {
        toast.error("Could not create Organization");
      }
    }
  };

  return (
    <div>
      <BreadCrumbs hasSubMenus={true} />
      <Card className="mt-3">
        <CardHeader className="bg-white">
          <h5>Create Organization</h5>
        </CardHeader>
        <CardBody>
          <Form onSubmit={handleSubmit(onSubmit)}>
            {/* Name Field */}
            <ControlledRowTextField
              label={"Name"}
              inputName={"displayName"}
              autoComplete={"off"}
              inputType={"text"}
              placeholder={"Enter Organization name"}
              control={control}
              rules={{ required: "Name is required." }}
              errors={errors}
            />
            <Row>
              <Col lg="4">{""}</Col>
              <Col lg="8">
                {errors.displayName && (
                  <span className="validation-message text-danger">
                    {errors.displayName.message}
                  </span>
                )}
              </Col>
            </Row>
            <hr />

            {/* Code Field */}
            <ControlledRowTextField
              label={"Code"}
              inputName={"code"}
              autoComplete={"off"}
              inputType={"text"}
              placeholder={"Enter Organization code"}
              control={control}
              rules={{ required: "Code is required." }}
              errors={errors}
            />
            <Row>
              <Col lg="4">{""}</Col>
              <Col lg="8">
                {errors.code && (
                  <span className="validation-message text-danger">
                    {errors.code.message}
                  </span>
                )}
              </Col>
            </Row>
            <hr />

            {/* Status Field */}
            {/* <ControlledSelect
              label="Status"
              inputName="status"
              control={control}
              optionsList={STATUS_OPTIONS}
              rules={{ required: "Please select status." }}
              errors={errors}
              placeholder="Select status"
              isClear={false}
            />
            <Row>
              <Col lg="4">{""}</Col>
              <Col lg="8">
                {errors.status && (
                  <span className="validation-message text-danger ">
                    {errors.status.message}
                  </span>
                )}
              </Col>
            </Row> */}

            {/* Delivery Point Checkbox */}
            <Row className="mb-3">
              <Col lg="4">
                <label className="form-label">Delivery Point</label>
              </Col>
              <Col lg="8">
                <input
                  type="checkbox"
                  {...register("supportsDeliveryPoint")}
                  className="form-check-input"
                />
              </Col>
            </Row>
            <hr />

            {/* Conditional Delivery Address Fields */}
            {supportsDeliveryPoint && (
              <>
                <ControlledRowTextFieldAddress
                  label={"Delivery Address"}
                  inputName={"deliveryAddress"}
                  inputType={"text"}
                  placeholder={"Select Delivery Address From Location Button"}
                  control={control}
                  setShowModal={setShowModal}
                  rules={{ required: "Delivery address is required" }}
                  errors={errors}
                />
                <Row>
                  <Col lg="4">{""}</Col>
                  <Col lg="8">
                    {errors.deliveryAddress && (
                      <span className="validation-message text-danger">
                        {errors.deliveryAddress.message}
                      </span>
                    )}
                  </Col>
                </Row>
                <hr />

                {/* Location Selection Button
                <Row className="mb-3">
                  <Col lg="4"></Col>
                  <Col lg="8">
                    <ButtonComponent
                      icon={<FontAwesomeIcon icon={faMapMarkerAlt} />}
                      type="button"
                      titel="Select Location"
                      onClick={() => setShowModal(true)}
                    />
                  </Col>
                </Row> */}

                {/* Hidden Latitude/Longitude Fields */}
                <Row className="d-none">
                  <Col lg="6">
                    <ControlledRowTextField
                      label={"Delivery Latitude"}
                      inputName={"deliveryLatitude"}
                      inputType={"number"}
                      placeholder={"Enter Latitude"}
                      control={control}
                      isDisabled={true}
                    />
                  </Col>
                  <Col lg="6">
                    <ControlledRowTextField
                      label={"Delivery Longitude"}
                      inputName={"deliveryLongitude"}
                      inputType={"number"}
                      placeholder={"Enter Longitude"}
                      control={control}
                      isDisabled={true}
                    />
                  </Col>
                </Row>

                {/* Delivery Contact Fields */}
                <ControlledRowTextField
                  label={"Delivery Contact Name"}
                  inputName={"deliveryContactName"}
                  autoComplete={"off"}
                  inputType={"text"}
                  placeholder={"Enter delivery contact name"}
                  control={control}
                  rules={{ required: "Delivery contact name is required" }}
                  errors={errors}
                />
                <Row>
                  <Col lg="4">{""}</Col>
                  <Col lg="8">
                    {errors.deliveryContactName && (
                      <span className="validation-message text-danger">
                        {errors.deliveryContactName.message}
                      </span>
                    )}
                  </Col>
                </Row>
                <hr />

                <ControlledRowTextField
                  label={"Delivery Contact Phone"}
                  inputName={"deliveryContactPhone"}
                  autoComplete={"off"}
                  inputType={"text"}
                  placeholder={"Enter delivery contact phone"}
                  control={control}
                  rules={{ required: "Delivery contact phone is required" }}
                  errors={errors}
                />
                <Row>
                  <Col lg="4">{""}</Col>
                  <Col lg="8">
                    {errors.deliveryContactPhone && (
                      <span className="validation-message text-danger">
                        {errors.deliveryContactPhone.message}
                      </span>
                    )}
                  </Col>
                </Row>
                <hr />
              </>
            )}

            {/* Allow Credit Orders Checkbox */}
            <Row className="mb-3">
              <Col lg="4">
                <label className="form-label">Allow Credit Orders</label>
              </Col>
              <Col lg="8">
                <input
                  type="checkbox"
                  {...register("allowCreditOrders")}
                  className="form-check-input"
                />
              </Col>
            </Row>
            <hr />

            {/* Conditional Credit Fields */}
            {allowCreditOrders && (
              <>
                <ControlledRowTextField
                  label={"Credit Limit"}
                  inputName={"creditLimit"}
                  autoComplete={"off"}
                  inputType={"number"}
                  placeholder={"Enter credit limit"}
                  control={control}
                  rules={{ required: "Credit limit is required" }}
                  errors={errors}
                />
                <Row>
                  <Col lg="4">{""}</Col>
                  <Col lg="8">
                    {errors.creditLimit && (
                      <span className="validation-message text-danger">
                        {errors.creditLimit.message}
                      </span>
                    )}
                  </Col>
                </Row>
                <hr />

                <ControlledRowTextField
                  label={"Credit Terms (Days)"}
                  inputName={"creditTermsDays"}
                  autoComplete={"off"}
                  inputType={"number"}
                  placeholder={"Enter credit terms in days"}
                  control={control}
                  rules={{ required: "Credit terms is required" }}
                  errors={errors}
                />
                <Row>
                  <Col lg="4">{""}</Col>
                  <Col lg="8">
                    {errors.creditTermsDays && (
                      <span className="validation-message text-danger">
                        {errors.creditTermsDays.message}
                      </span>
                    )}
                  </Col>
                </Row>
                <hr />

                <ControlledRowTextField
                  label={"Member Credit Limit"}
                  inputName={"memberCreditLimit"}
                  autoComplete={"off"}
                  inputType={"number"}
                  placeholder={"Enter member credit limit"}
                  control={control}
                  rules={{ required: "Member credit limit is required" }}
                  errors={errors}
                />
                <Row>
                  <Col lg="4">{""}</Col>
                  <Col lg="8">
                    {errors.memberCreditLimit && (
                      <span className="validation-message text-danger">
                        {errors.memberCreditLimit.message}
                      </span>
                    )}
                  </Col>
                </Row>
                <hr />
              </>
            )}

            {/* Submit Button */}
            <div className="d-flex justify-content-end">
              {isSubmitLoading || isCreating ? (
                <ButtonComponent
                  titel="Saving..."
                  isDisabled={true}
                  icon={<FontAwesomeIcon icon={faSpinner} spin />}
                />
              ) : (
                <ButtonComponent titel="Submit" type="submit" />
              )}
            </div>
          </Form>

          {/* Location Selection Modal */}
          <Modal
            show={showModal}
            onHide={() => setShowModal(false)}
            centered
            size="lg"
            backdrop="static"
            keyboard={false}
          >
            <Modal.Header closeButton>
              <Modal.Title>Select Delivery Location</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Map onSaveLocation={handleLocationSave} />
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => handleManualLocationSave()}
              >
                Save Location
              </Button>
            </Modal.Footer>
          </Modal>
        </CardBody>
      </Card>
    </div>
  );
};

export default CreateOrg;
